﻿import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  MenuItem,
  Select,
  DialogTitle,
  Dialog,
  DialogContent,
  FormControlLabel,
  Paper,
} from "@mui/material";
import axios from "@/features/axiosHelper";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFileImportPopupOpen,
  setFileImportPopupOpen,
  selectFileImportOptions,
  getSelectedDataType,
  fetchFileImportDataTypes,
  setSelectedDataType,
  getTabType,
  importTabTypesEnum,
} from "@/features/maintenance/fileImportSlice";
import "./FileImportPopup.css";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import { CloseIconFill } from "@/ImageRepo";
import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepDragDropUpload from "@/components/GenericComponents/ShepDragDropUpload";

const FileImportPopup = ({
  onClose,
  onUploadSuccess,
  allowDuplicates = true,
}) => {
  const dispatch = useDispatch();
  const popupOpen = useSelector(selectFileImportPopupOpen);
  const options = useSelector(selectFileImportOptions);
  const selectedDataType = useSelector(getSelectedDataType);
  const tabType = useSelector(getTabType);
  const [hasSubDataError, setHasSubDataError] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [approvedFileImports, setApprovedFileImports] = useState([]);
  const [cancelledFiles, setCancelledFiles] = useState([]);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [hasHeaders, setHasHeaders] = useState(true);

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // Currently not used, can be set when adding authorization to import

  const errorMsg = "Must select type to import";

  useEffect(() => {
    if (popupOpen) {
      dispatch(fetchFileImportDataTypes());
      GetUser();
    }
  }, [popupOpen, tabType]);

  const closePopup = () => {
    dispatch(
      setFileImportPopupOpen({ open: false, tabType: null, dataType: -1 }),
    );

    setHasSubDataError(false);
    setFiles([]);
    setFilesUploaded(false);
    setRejectedFiles([]);
    setApprovedFileImports([]);
    setCancelledFiles([]);
    if (onClose) {
      onClose();
    }
  };
  const manageUploadingFiles = ({ newFiles }) => {
    const oldFiles = [...files];
    var acceptedNewFiles = [];

    if (allowDuplicates) {
      acceptedNewFiles = [...newFiles];
    } else {
      Array.from(newFiles).forEach((file) => {
        let newFileName = file.name;
        let duplicateCount = 1;

        // Check for duplicate names
        while (
          [...oldFiles, ...acceptedNewFiles].some(
            (existingFile) => existingFile.name === newFileName,
          )
        ) {
          const [baseName, extension] = file.name.includes(".")
            ? [
                file.name.substring(0, file.name.lastIndexOf(".")),
                file.name.substring(file.name.lastIndexOf(".")),
              ]
            : [file.name, ""];

          newFileName = `${baseName} (${duplicateCount})${extension}`;
          duplicateCount++;
        }

        // Create a new File object with the updated name since files are immutable.
        const renamedFile = new File([file], newFileName, { type: file.type });

        acceptedNewFiles.push(renamedFile);
      });
    }

    const updatedFiles = [...oldFiles, ...acceptedNewFiles];
    setFiles(updatedFiles);
  };

  const removeFileFromUpload = ({ file }) => {
    const filteredFiles = files.filter((x) => x !== file);
    setFiles(filteredFiles);
  };
  const handleImport = async () => {
    //send to backend
    if (selectedDataType === -1) {
      setHasSubDataError(true);
      return;
    }
    if (
      tabType === importTabTypesEnum.catalog ||
      tabType === importTabTypesEnum.crossReference ||
      tabType === importTabTypesEnum.workflowLookup
    ) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("Files", files[i]);
      }
      formData.append("DataType", selectedDataType);
      formData.append("HasHeaders", hasHeaders);
      formData.append("ReplaceExisting", replaceExisting);
      formData.append("TabType", tabType);
      formData.append("User", user);
      await axios
        .post("/fileimport", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setFilesUploaded(true);
          setRejectedFiles(response.data.rejectedFiles);
          setApprovedFileImports(response.data.approvedFileImports);
          if (onUploadSuccess) {
            onUploadSuccess();
          }

          // heap.io
          switch (tabType) {
            case importTabTypesEnum.crossReference:
              window.heap.track("Cross References - Import Files");
              break;
            case importTabTypesEnum.catalog:
              window.heap.track("Catalog Data - Import Files");
              break;
          }
        })
        .catch((e) => {
          dispatch(
            createAlert({
              status: "Error",
              message: "Unable to import: " + e.message,
            }),
          );
        });
    } else if (tabType === importTabTypesEnum.fileManager) {
      const formData = new FormData();
      var rejectedFiles = [];
      files.forEach((file) => {
        // Max file length for inbound files is 10 MB (10000 KB)
        if (file.size < 10000000) {
          formData.append("Files", file);
        } else {
          rejectedFiles.push(file);
        }
      });

      formData.append("DataType", selectedDataType);
      formData.append("TabType", tabType);
      formData.append("User", user);
      await axios
        .post("/fileimport", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          setFilesUploaded(true);
          setRejectedFiles(rejectedFiles);
          setApprovedFileImports(response.data.approvedFileImports);
          if (onUploadSuccess) {
            onUploadSuccess();
          }

          // heap.io
          window.heap.track("File Manager - Upload Files");
        })
        .catch((e) => {
          const messages = [e?.response?.data || e.message];
          if (rejectedFiles.length > 0) {
            messages.push("One or more files exceed the 10 MB limit");
          }
          const errorMessage = messages.join(". ");
          dispatch(
            createAlert({
              status: "Error",
              message: errorMessage,
            }),
          );
        });
    }
  };
  const cancelFileImport = async ({ file }) => {
    if (
      tabType === importTabTypesEnum.catalog ||
      tabType === importTabTypesEnum.crossReference ||
      tabType === importTabTypesEnum.workflowLookup
    ) {
      await axios
        .post("/fileimport/cancel", null, {
          params: { fileImportId: file.FileImportId, fileName: file.FileName },
        })
        .then((response) => {
          setCancelledFiles([...cancelledFiles, file.FileImportId]);
        })
        .catch((e) => {
          onError(e.message);
        });
    } else if (tabType === importTabTypesEnum.fileManager) {
      await axios
        .post("/fileimport/cancel-edi-file", null, {
          params: { fileId: file.FileId },
        })
        .then((response) => {
          setCancelledFiles([...cancelledFiles, file.FileId]);
        })
        .catch((e) => {
          console.log("Error: ", e);
          onError(e.response.data);
        });
    }
  };

  const onError = (message) => {
    dispatch(
      createAlert({
        status: "Error",
        message: "Unable to cancel import: " + message,
      }),
    );
  };

  const GetUser = () => {
    axios
      .get(`/account/user-profile`)
      .then((response) => {
        if (response.data == null) {
          return;
        }
        setUser(response.data.LeapFrogBetaUser.UserName);
        setRole(response.data.Role);
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <Dialog
        maxWidth={"lg"}
        fullWidth={true}
        open={popupOpen}
        onClose={closePopup}
        PaperProps={{
          style: {
            backgroundColor: "var(--disabled)",
            boxShadow: "none",
            height: "800px",
          },
        }}
      >
        <CloseIconFill
          className="file-import-close-icon"
          onClick={closePopup}
        />
        <Paper className="import-container">
          <DialogTitle className="flex-center-aligned">
            <Typography variant="h2">Import</Typography>
          </DialogTitle>
          <DialogContent className="flex-center-aligned">
            {!filesUploaded ? (
              <Container
                className="flex-center-aligned"
                sx={{
                  flexDirection: "column",
                }}
              >
                {hasSubDataError && (
                  <Typography variant={"caption"} sx={{ color: "red" }}>
                    {errorMsg}
                  </Typography>
                )}
                {tabType !== importTabTypesEnum.fileManager ? (
                  <Container
                    className="flex-center-aligned"
                    sx={{
                      flexDirection: "column",
                    }}
                  >
                    <Select
                      value={selectedDataType}
                      onChange={(e) => {
                        setHasSubDataError(false);
                        dispatch(
                          setSelectedDataType({ value: e.target.value }),
                        );
                      }}
                      error={hasSubDataError}
                      sx={{ marginBottom: "20px", width: "100%" }}
                    >
                      <MenuItem key={-1} value={-1}>
                        Select a Type
                      </MenuItem>
                      {Object.keys(options).map((x) => {
                        return (
                          <MenuItem key={x} value={x}>
                            {options[x]}
                          </MenuItem>
                        );
                      })}
                    </Select>

                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "left",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <ShepCheckbox
                            checked={true}
                            disabled
                            //onClick={() => setHasHeaders(!hasHeaders)}
                          />
                        }
                        label={"documents contain headers"}
                      />
                      <FormControlLabel
                        control={
                          <ShepCheckbox
                            checked={replaceExisting}
                            onClick={() => setReplaceExisting(!replaceExisting)}
                          />
                        }
                        label={"update existing records"}
                      />
                    </Box>
                  </Container>
                ) : null}
                <Container
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    flexGrow: "1",
                    flexDirection: "column",
                  }}
                >
                  {Array.from(files).map((x) => {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "var(--fields-focused)",
                          marginBottom: "15px",
                        }}
                      >
                        {x.name}
                        <Button
                          sx={{ minWidth: "20px!important" }}
                          onClick={() => removeFileFromUpload({ file: x })}
                        >
                          x
                        </Button>
                      </Box>
                    );
                  })}
                </Container>

                <ShepDragDropUpload
                  onFilesSelected={manageUploadingFiles}
                  accept={
                    tabType === importTabTypesEnum.catalog ||
                    tabType === importTabTypesEnum.crossReference ||
                    tabType === importTabTypesEnum.workflowLookup
                      ? ".xlsx, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      : ""
                  }
                  supportedFileMessage={
                    tabType === importTabTypesEnum.catalog ||
                    tabType === importTabTypesEnum.crossReference ||
                    tabType === importTabTypesEnum.workflowLookup
                      ? "CSV, Excel File"
                      : ""
                  }
                />
                <ShepButton
                  variant="primary"
                  onClick={handleImport}
                  sx={{ width: "100%", marginTop: "20px" }}
                  disabled={files.length === 0}
                >
                  Import Files
                </ShepButton>
                <ShepButton
                  variant="secondary"
                  onClick={closePopup}
                  sx={{ width: "100%", marginTop: "10px" }}
                >
                  Close
                </ShepButton>
              </Container>
            ) : (
              <Container
                className="flex-center-aligned"
                sx={{
                  flexDirection: "column",
                }}
              >
                <Box
                  component="label"
                  className="flex-center-aligned"
                  sx={{
                    height: "300px",
                    backgroundColor: "var(--tertiary-14)",
                    flexGrow: "1",
                    flexDirection: "column",
                    padding: "0px 20px",
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="contentAccent1">
                    Your Import Process is Currently in Progress.
                  </Typography>
                  {tabType === importTabTypesEnum.fileManager ? (
                    <Typography style={{ marginTop: "30px" }}>
                      You can close this screen if you wish and track your
                      import file using the File Manager or in the Notifications
                      tab.
                    </Typography>
                  ) : (
                    <Typography style={{ marginTop: "30px" }}>
                      You can close this screen if you wish and track your
                      import file on the banner at the top of the screen or in
                      the Notifications tab.
                    </Typography>
                  )}
                </Box>
                <Container
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    flexGrow: "1",
                    flexDirection: "column",
                  }}
                >
                  {Array.from(approvedFileImports).map((x) => {
                    const cancelled =
                      cancelledFiles.includes(x.FileId) ||
                      cancelledFiles.includes(x.FileImportId);
                    return (
                      <>
                        <Typography variant={"caption"} sx={{ color: "red" }}>
                          {cancelled ? "Import cancelled." : ""}
                        </Typography>
                        <Box
                          className={
                            cancelled ? "file-bar rejected" : "file-bar "
                          }
                        >
                          {x.FileName}
                          <Button
                            disabled={cancelled}
                            onClick={() => cancelFileImport({ file: x })}
                          >
                            x
                          </Button>
                        </Box>
                      </>
                    );
                  })}
                  {Array.from(rejectedFiles).map((x) => {
                    return (
                      <>
                        <Typography variant={"caption"} sx={{ color: "red" }}>
                          {
                            "Acceptable files are .xlsx, .csv. File size limit is 10MB"
                          }
                        </Typography>
                        <Box className={"file-bar rejected"}>
                          {x?.name || x}
                          <Button disabled={true}>x</Button>
                        </Box>
                      </>
                    );
                  })}
                </Container>
                <ShepButton
                  variant="primary"
                  onClick={closePopup}
                  sx={{ width: "100%", marginTop: "20px" }}
                  disabled={files.length === 0}
                >
                  Close
                </ShepButton>
              </Container>
            )}
          </DialogContent>
        </Paper>
      </Dialog>
    </>
  );
};

export default FileImportPopup;
